/** Dashboards - **four fixed views over existing reads** (ADR-0195, as amended 2026-11-01; v5 stage
 * F5).
 *
 * Executive, Data health, Operations and Suppliers. The list is closed (`DASHBOARD_VIEWS`, asserted
 * by `Dashboards.test.tsx`); a fifth is an amendment to the ADR, so that sprawl needs a decision
 * and not a pull request. **A tile is a view and never a source**: each calls an endpoint another
 * screen already uses, renders what it returns, and opens the receipt drawer or the list the other
 * screens open. There is no customer-built tile and no layout editor (ADR-0194).
 *
 * **What is not here, and why it is said rather than left as a gap** (`DASHBOARD_NOT_HERE`): the
 * year-end illustration and the risk placement grid of ADR-0195 3a and 3b are allowed *under a
 * shown-correctly checklist* whose reads do not exist yet (a boundary-version read, a restatement
 * check, a scale on each risk); intensity and movers wait for ADR-0191; annotations wait for their
 * own table. An uncertainty band waits for a verification-log row. None is drawn.
 *
 * The breakdown drawer is Home's: the total and each scope open the figures they summed, a page at
 * a time, and each figure opens its receipt (rule 3).
 */

import { useMe } from "../api/auth";
import { useHomeSummary } from "../api/homeSummary";
import { useFiguresPaged, useInventory, useInventoryMonthly } from "../api/queries";
import type { FigureCut } from "../api/queries";
import { SquaresFour } from "@phosphor-icons/react";

import { DashboardBand } from "../components/ui/DashboardBand";
import { EmptyAction } from "../components/ui/EmptyAction";
import { EmptyState } from "../components/ui/EmptyState";
import { HowDrawer } from "../components/ui/HowDrawer";
import { InfoPopover } from "../components/ui/InfoPopover";
import { TablePage } from "../components/ui/TablePage";
import { DashTiles } from "../components/ui/Tile";
import { RequireSession } from "../shell/RequireSession";
import { mayEnterData } from "../shell/routeTable";
import styles from "./dashboards/Dashboards.module.css";
import { DataHealthView } from "./dashboards/DataHealthView";
import { DASHBOARD_VIEWS, DASH_LABELS, nothingToShowYet } from "./dashboards/dashboardModel";
import { monthOfBreakdown, useMonthDrawer } from "./bands/monthBreakdown";
import { ExecutiveView } from "./dashboards/ExecutiveView";
import { OperationsView } from "./dashboards/OperationsView";
import { SuppliersView } from "./dashboards/SuppliersView";
import { pagingOf, WHOLE_RECORDS_NOTE } from "./drawerFigures";
import { NoPeriod } from "./NoPeriod";
import { ScreenFrame } from "./ScreenFrame";
import { keepSearch } from "./search";
import { useScreen } from "./useScreen";
import { useUrlTab } from "./useUrlTab";

export function DashboardsScreen() {
  return (
    <RequireSession>
      <DashboardsBody />
    </RequireSession>
  );
}

function DashboardsBody() {
  const screen = useScreen();
  const [view] = useUrlTab(DASHBOARD_VIEWS.map(({ key, label }) => ({ key, label })));
  const about = DASHBOARD_VIEWS.find((candidate) => candidate.key === view);

  // The drawer is Home's: the total and each scope, from the server, a page at a time.
  const inventory = useInventory(screen.window, screen.method);
  const summary = useHomeSummary(screen.window, screen.method);
  const role = useMe().data?.role;
  const quiet = Boolean(screen.window) && nothingToShowYet(inventory.data, summary.data);
  const key = screen.breakdown ?? "";
  const scopeKey = /^scope-([123])$/.exec(key);
  const cut: FigureCut = scopeKey ? { scope: scopeKey[1] as "1" | "2" | "3" } : {};
  // A month in the Executive chart opens the same drawer on that month (design uplift U5).
  const monthKey = monthOfBreakdown(screen.breakdown);
  const monthly = useInventoryMonthly(monthKey ? screen.window : undefined, screen.method);
  const monthDrawer = useMonthDrawer({
    key: screen.breakdown,
    window: screen.window,
    method: screen.method,
    months: monthly.data?.months,
    label: "Every figure",
  });
  const breakdownRead = useFiguresPaged(
    screen.breakdown && !monthKey ? screen.window : undefined,
    screen.method,
    cut,
  );
  // The headline is the server's `total_kgco2e` (finding 8), never a sum of the cards made here; a
  // scope's is that card's own figure. Both are `undefined` until the read lands or when it fails.
  const headline = scopeKey
    ? inventory.data?.totals_by_scope.find((card) => card.scope === scopeKey[1])?.kgco2e
    : inventory.data?.total_kgco2e;
  const label = scopeKey ? `Scope ${scopeKey[1]}` : "Every figure";

  return (
    <ScreenFrame
      screen={screen}
      title="Dashboards"
      {...(monthDrawer ?? {
        breakdownFigures: breakdownRead.figures,
        breakdownLabel: label,
        breakdownRead,
        breakdownPaging: pagingOf(breakdownRead),
        breakdownHeadline: headline,
        breakdownNote: headline !== undefined ? WHOLE_RECORDS_NOTE : undefined,
      })}
    >
      <TablePage
        icon={SquaresFour}
        title="Dashboards"
        label={about?.label}
        actions={
          <>
            <InfoPopover infoKey="dashboards.views" />
            <HowDrawer infoKey="dashboards.not-here" label="What these views leave out" />
          </>
        }
        tiles={
          !screen.window || quiet ? (
            <DashTiles labels={DASH_LABELS[view]} label={`${about?.label ?? "Dashboard"} summary`} />
          ) : undefined
        }
        state={
          !screen.window ? (
            <NoPeriod />
          ) : quiet ? (
            <EmptyState
              icon={SquaresFour}
              title="Nothing to show yet"
              compact
              testId="dashboards-empty"
              action={
                <EmptyAction
                  label="Upload a bill"
                  allowed={role === undefined ? undefined : mayEnterData(role)}
                  reason="A Contributor, Approver or Admin uploads bills."
                  to="/upload"
                  search={keepSearch}
                  plus={false}
                  testId="dashboards-empty-upload"
                />
              }
            >
              Dashboards fill in as bills arrive.
            </EmptyState>
          ) : undefined
        }
        band={
          !screen.window || quiet ? undefined : (
            <DashboardBand
              label={`${about?.label ?? "Dashboard"} dashboard`}
              tiles={
                <div className={styles.area}>
                  {view === "executive" ? (
                    <ExecutiveView screen={screen} />
                  ) : view === "data-health" ? (
                    <DataHealthView screen={screen} />
                  ) : view === "operations" ? (
                    <OperationsView screen={screen} />
                  ) : (
                    <SuppliersView screen={screen} />
                  )}
                </div>
              }
            />
          )
        }
      />
    </ScreenFrame>
  );
}
